import rewardPlaceholder from '../../assets/v3/reward-placeholder.svg'
import { formatPoints } from './points-format'
import { t } from '../../i18n'

/** What the badge on a reward row says, which is the whole of its state.
 *
 * Four, from the designer's note ("Rewards have 3 states" plus the applied case):
 *
 *  - `use`         affordable and not yet on the ticket — the only actionable one
 *  - `unaffordable` the customer cannot pay for it yet, so the badge shows the price
 *  - `applied`     already on this ticket; green, because brand blue on this screen
 *                  means "available to spend" and this one is spent
 *  - `unavailable` the shop cannot serve rewards at all. Per the note this "only
 *                  appears if the shop has canceled Badger (or the grace period has
 *                  ended)", which is the same billing gate the backend enforces as
 *                  LOCATION_BLOCKED / LOCATION_OFF — so it is a property of the
 *                  location, never of the reward, and every row shows it at once.
 */
export type RewardState = 'use' | 'unaffordable' | 'applied' | 'unavailable'

export interface RewardOption {
  id: string
  name: string
  pointsCost: number
  /** Served by the backend (DEV-401/DEV-417). Absent rewards fall back to the design's
   * generic badge rather than a broken image. */
  imageUrl?: string | null
  state: RewardState
  /** The designer wants a birthday reward shown first and marked. */
  birthday?: boolean
}

export function RewardCard({
  reward,
  onUse,
}: {
  reward: RewardOption
  onUse: (reward: RewardOption) => void
}) {
  const actionable = reward.state === 'use'

  return (
    <button
      type="button"
      disabled={!actionable}
      onClick={() => onUse(reward)}
      className="flex w-full items-center gap-[16px] rounded-[11px] bg-white py-[16px] pl-[19px] pr-[18px] text-left disabled:cursor-default"
    >
      <img
        src={reward.imageUrl || rewardPlaceholder}
        alt=""
        className="size-[72px] shrink-0 rounded-full object-cover"
      />
      <span className="flex min-w-0 flex-1 items-center justify-between gap-[8px]">
        <span className="flex min-w-0 flex-col items-start gap-[4px]">
          <span className="truncate text-[18px] font-semibold leading-[20px] text-brand">
            {reward.name}
          </span>
          {/* The price stays visible even when the badge is the action, so a cashier
           * reading the row aloud always has the number in front of them. */}
          <span className="text-[18px] font-semibold leading-[1.5] text-on-brand-faint">
            {reward.birthday ? t('v3BirthdayReward') : `${formatPoints(reward.pointsCost)} ${t('points')}`}
          </span>
        </span>
        <RewardBadge reward={reward} />
      </span>
    </button>
  )
}

function RewardBadge({ reward }: { reward: RewardOption }) {
  const label =
    reward.state === 'applied'
      ? t('applied')
      : reward.state === 'unavailable'
        ? t('v3Unavailable')
        : reward.state === 'use'
          ? t('v3Use')
          : `${formatPoints(reward.pointsCost)} ${t('points')}`

  const tone =
    reward.state === 'applied'
      ? 'bg-applied'
      : reward.state === 'unavailable'
        ? 'bg-muted'
        : 'bg-brand'

  return (
    <span
      className={`shrink-0 whitespace-nowrap rounded-[23px] px-[12px] py-[3px] text-[14px] font-medium leading-[1.5] text-white ${tone}`}
    >
      {label}
    </span>
  )
}
